import type { Transaction } from "@/apis/gql/types";
import { SmbAttachmentStatus } from "@/apis/gql/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { Clock, Link2, Link2Off, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

export type LinkStatus =
  | "attached"
  | "unattached"
  | "suggested"
  | "ai-in-progress";

export function getLinkStatusForOneTransaction(
  transaction: Pick<Transaction, "bankTxnId" | "smbAttachmentStatus">,
): LinkStatus {
  if (
    transaction.smbAttachmentStatus === SmbAttachmentStatus.ATTACHED ||
    transaction.smbAttachmentStatus === SmbAttachmentStatus.ATTACHED_ZERO_TOUCH
  ) {
    return "attached";
  }

  if (
    transaction.smbAttachmentStatus === SmbAttachmentStatus.ATTACHMENT_SUGGESTED
  ) {
    return "suggested";
  }
  return "unattached";
}

function getLinkStatusForTransactons(
  transactions: Pick<Transaction, "bankTxnId" | "smbAttachmentStatus">[],
): LinkStatus {
  if (!transactions || transactions.length === 0) {
    return "unattached";
  }

  if (
    transactions.every(
      (transaction) =>
        getLinkStatusForOneTransaction(transaction) === "attached",
    )
  ) {
    return "attached";
  }

  if (
    transactions.some(
      (transaction) =>
        getLinkStatusForOneTransaction(transaction) === "suggested",
    )
  ) {
    return "suggested";
  }

  return "unattached";
}

export function getLinkStatusForInvoice(
  transactions:
    | Pick<Transaction, "bankTxnId" | "smbAttachmentStatus">[]
    | undefined,
  reconTriggered: boolean | undefined,
  reconEnabled: boolean,
  invoiceStatus: InvoiceStatus,
): LinkStatus {
  if (transactions && transactions.length > 0) {
    return getLinkStatusForTransactons(transactions);
  }

  if (invoiceStatus === InvoiceStatus.NEW) {
    return "unattached";
  }

  if (reconEnabled && reconTriggered === false) {
    return "ai-in-progress";
  }

  return "unattached";
}

export function InvoiceTransactionLinkStatusIcon({
  linkStatus,
}: {
  linkStatus: LinkStatus;
}) {
  const { t } = useTranslation();
  const iconMap = {
    unattached: null,
    attached: Link2,
    suggested: Sparkles,
    "ai-in-progress": Clock,
  };

  const Icon = iconMap[linkStatus];

  return (
    <div className="flex items-center justify-center">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {Icon && <Icon className="h-5 w-5 text-gray-500" />}
          </TooltipTrigger>
          <TooltipContent className="bg-white text-gray-700 border border-gray-200 shadow-lg rounded-lg px-4 py-2 max-w-[330px]">
            <div className="flex flex-col gap-0.5">
              <div className="font-bold">
                {t(`invoice-transaction-link.tooltip.${linkStatus}.title`)}
              </div>
              <div className="font-normal">
                {t(
                  `invoice-transaction-link.tooltip.${linkStatus}.description`,
                )}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
